import type {
  AuditLogQuery,
  AuditLogStatus,
} from '@/features/AuditLogs/lib/auditSourceParams'
import { useMemo } from 'react'

export type DateRangePreset =
  | 'lastHour'
  | 'last24Hours'
  | 'last7Days'
  | 'last30Days'
  | 'allTime'

export interface AuditLogFilterParams {
  rangeEnd: number
  dateRangePreset?: DateRangePreset
  hiveId?: string | null
  orgId?: string | null
  user?: string | null
  status?: AuditLogStatus | null
  licenseId?: string | null
  userId?: string | null
}

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

const PRESET_DURATIONS_MS: Record<
  Exclude<DateRangePreset, 'allTime'>,
  number
> = {
  lastHour: HOUR_MS,
  last24Hours: DAY_MS,
  last7Days: 7 * DAY_MS,
  last30Days: 30 * DAY_MS,
}

function getDateRangeFromPreset(
  preset: DateRangePreset,
  rangeEnd: number,
): {
  from?: number
  to?: number
} {
  if (preset === 'allTime') {
    return {}
  }
  const duration = PRESET_DURATIONS_MS[preset]
  return { from: rangeEnd - duration, to: rangeEnd }
}

export function useAuditLogFilters(filterParams: AuditLogFilterParams) {
  const {
    rangeEnd,
    dateRangePreset = 'last24Hours',
    hiveId,
    orgId,
    user,
    status,
    licenseId,
    userId,
  } = filterParams

  const enhancedQueryParams = useMemo<AuditLogQuery>(() => {
    const { from, to } = getDateRangeFromPreset(dateRangePreset, rangeEnd)

    const params: AuditLogQuery = {}

    if (from !== undefined) {
      params.from = from
    }
    if (to !== undefined) {
      params.to = to
    }

    if (hiveId) {
      params.hiveId = hiveId
    }

    if (orgId) {
      params.orgId = orgId
    }

    if (user) {
      params.user = user
    }

    if (status) {
      params.status = status
    }

    if (licenseId) {
      params.licenseId = licenseId
    }

    if (userId) {
      params.userId = userId
    }

    return params
  }, [
    rangeEnd,
    dateRangePreset,
    hiveId,
    orgId,
    user,
    status,
    licenseId,
    userId,
  ])

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        hiveId ||
          orgId ||
          user ||
          status ||
          licenseId ||
          userId ||
          dateRangePreset !== 'last24Hours',
      ),
    [hiveId, orgId, user, status, licenseId, userId, dateRangePreset],
  )

  return {
    enhancedQueryParams,
    hasActiveFilters,
  }
}
